import { createServicesManager, createServicesMap } from '@wix/services-manager'
import type { ServicesManager } from '@wix/services-manager/types'
import type { ServiceProvider } from './types'

/**
 * Build a `ServicesManager` from an already-resolved list of service providers.
 * This is the path thunderbolt's IoC container feeds (it collects providers via
 * `multi(ServiceProviderSymbol)` and hands the array here).
 */
export const createServicesManagerFromProviders = (
	providers: Array<ServiceProvider>,
	parentServicesManager?: ServicesManager
): ServicesManager => createServicesManager(createServicesMap(providers), parentServicesManager)

type ImplOrWrapped = ServiceProvider['impl'] | { impl: ServiceProvider['impl'] }

const resolveImpl = (entry: ImplOrWrapped): ServiceProvider['impl'] =>
	typeof entry === 'object' && entry !== null && 'impl' in entry ? entry.impl : entry

/**
 * Build a `ServicesManager` from plain data maps, with no IoC container involved.
 * Mirrors thunderbolt-platform's `servicesLoader`: `definitionToConfig` supplies the config
 * per service definition, `definitionToImpl` supplies the factory (either directly or wrapped
 * as `{ impl }`, matching the generated `services.ts` shape). Intended for hosts (or other
 * repos) that resolve services without thunderbolt's per-service IoC adapters.
 */
export const createServicesManagerFromConfig = (
	definitionToConfig: Record<string, any>,
	definitionToImpl: Record<string, ImplOrWrapped>,
	parentServicesManager?: ServicesManager
): ServicesManager =>
	createServicesManagerFromProviders(
		Object.entries(definitionToConfig).map(([definition, config]) => ({
			definition,
			config,
			impl: resolveImpl(definitionToImpl[definition]),
		})) as Array<ServiceProvider>,
		parentServicesManager
	)

/**
 * Add providers to an existing manager, skipping any whose definition is already registered.
 * Used by dynamic/external loading to avoid double-registering a service.
 */
export const addServicesIfMissing = (servicesManager: ServicesManager, providers: Array<ServiceProvider>): void => {
	const missing = providers.filter((provider) => !servicesManager.hasService(provider.definition))
	if (missing.length > 0) {
		servicesManager.addServices(createServicesMap(missing))
	}
}
